<script lang="ts">
import { useRouter } from 'vue-router'
import { defineComponent } from 'vue'
import PageBreadcrumb from '@/components/common/PageBreadcrumb.vue'
import PostDataService from '@/services/PostDataService'
import PostCategoryDataService, { type PostCategory } from '@/services/PostCategoryDataService'
import type Post from '@/types/Post'
import type ResponseData from '@/types/ResponseData'
import { toast } from 'vue3-toastify'
import 'vue3-toastify/dist/index.css'
import * as _ from 'lodash'
import { Table, TableHeader, TableBody, TableRow, TableCell } from '@/components/ui/table'
import { TableCard, TableSearch, TablePagination, TableEmptyState } from '@/components/common/table'
import { Button } from '@/components/ui/button'
import { Pencil, Plus, Trash2 } from 'lucide-vue-next'

export default defineComponent({
  name: 'post-list',
  components: {
    Table,
    TableHeader,
    TableBody,
    TableRow,
    TableCell,
    TableCard,
    TableSearch,
    TablePagination,
    TableEmptyState,
    Button,
    Pencil,
    Plus,
    Trash2,
    PageBreadcrumb,
  }, data() {
    return {
      posts: [] as Post[],
      currentPost: {} as Post,
      router: useRouter(),
      filterPosts: '',
      loaded: false,
      pagination: {} as any,
      postCategories: [] as PostCategory[],
      showDeleteModal: false,
      postToDelete: null as Post | null
    }
  }, methods: {
    async loadCategories() {
      try {
        const response = await PostCategoryDataService.getAll()
        this.postCategories = (response.data as any)?.data || response.data
      } catch (error: unknown) {
        console.error('Error loading categories:', error)
        const errorMessage = error instanceof Error ? error.message : 'Neznáma chyba'
        toast.error('Chyba pri načítavaní kategórií: ' + errorMessage)
      }
    },

    getCategoryName(categoryId: number | undefined): string {
      if (!categoryId) return 'Nezaradené'
      const category = this.postCategories.find(cat => cat.id === categoryId)
      return category ? category.name : `ID: ${categoryId}`
    },

    retrievePosts(page: number, type: string) {
      if (type == 'prev') {
        page--;
      } else if (type == 'next') {
        page++;
      }

      PostDataService.getAllPosts(page)
        .then((response: ResponseData) => {
          const envelope = (response.data as any)?.data
          this.posts = Array.isArray(envelope) ? envelope : (envelope?.data as any[])
          this.pagination = (response.data as any)?.data
          this.loaded = true
        })
        .catch((e: Error) => {
          toast.error('Chyba: ' + e.message)
          this.loaded = true
        })
    },
    refreshList() {
      this.retrievePosts(1, '')
      this.currentPost = {} as Post
    }, addPost() {
      this.router.push({
        name: 'Pridať článok',
      })
    }, stripHtml(content: string): string {
      // Remove HTML tags and markdown formatting for display
      return content
        .replace(/<[^>]*>/g, '') // Remove HTML tags
        .replace(/[#*_`]/g, '') // Remove markdown formatting
        .replace(/\n/g, ' ') // Replace newlines with spaces
        .trim()
        .substring(0, 100) + (content.length > 100 ? '...' : '') // Truncate
    }, async confirmDelete(post: Post) {
      if (!post.id) {
        toast.error('Chyba: ID článku nie je definované')
        return
      }
      this.postToDelete = post
      this.showDeleteModal = true
    },

    async deletePost() {
      if (!this.postToDelete?.id) {
        toast.error('Chyba: ID článku nie je definované')
        return
      }

      try {
        await PostDataService.delete(this.postToDelete.id)
        toast.success('Článok bol úspešne zmazaný')
        this.showDeleteModal = false
        this.postToDelete = null
        this.refreshList()
      } catch (error: unknown) {
        console.error('Error deleting post:', error)
        const errorMessage = error instanceof Error ? error.message : 'Neznáma chyba'
        toast.error('Chyba pri mazaní článku: ' + errorMessage)
      }
    },

    cancelDelete() {
      this.showDeleteModal = false
      this.postToDelete = null
    },
  },
  computed: {
    filteredPosts() {
      return this.posts.filter(post => {
        const title = post.title.toLowerCase()
        const category_id = post.category_id?.toString() || ''
        if (!this.filterPosts) return false
    const searchTerm = this.filterPosts.toLowerCase()

    return title.includes(searchTerm) || category_id.includes(searchTerm)
      })
    },
  }, mounted() {
    Promise.all([
      this.retrievePosts(1, ''),
      this.loadCategories()
    ])
  },
})
</script>

<template>
  <main class="flex-1">
    <div class="py-6">
      <div class="max-w-(--breakpoint-2xl) mx-auto">
        <PageBreadcrumb page-title="Zoznam článkov" />
      </div>
      <TableCard title="Zoznam článkov">
        <template #controls>
          <div class="flex w-full items-center gap-3 sm:w-auto">
            <div class="flex-1 sm:w-64 sm:flex-initial md:w-72">
              <TableSearch v-model="filterPosts" />
            </div>
            <Button class="shrink-0" @click="addPost()">
              <Plus />
              <span>Pridať článok</span>
            </Button>
          </div>
        </template>
          <div class="max-w-full overflow-x-auto min-w-0">
            <Table class="min-w-full divide-y divide-gray-100 dark:divide-gray-800">
              <TableHeader
                class="border-y border-gray-100 bg-gray-50 dark:border-gray-800 dark:bg-gray-900"
              >
                <TableRow>
                  <TableCell is-header class="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400">
                    ID
                  </TableCell>
                  <TableCell is-header class="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400">
                    Nadpis
                  </TableCell>
                  <TableCell is-header class="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400">
                    Úryvok
                  </TableCell>
                  <TableCell is-header class="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400">
                    Kategória
                  </TableCell>
                  <TableCell is-header class="px-6 py-3 text-start text-xs font-medium text-gray-500 dark:text-gray-400 text-center">
                    Akcie
                  </TableCell>
                </TableRow>
              </TableHeader>
                    <TableBody class="divide-y divide-gray-100 dark:divide-gray-800">
                      <TableRow class="hover:bg-gray-200 dark:hover:bg-gray-700" v-for="post in filteredPosts" :key="post.id">
                        <TableCell class="px-6 py-4 text-sm font-medium text-gray-800 dark:text-white/90">{{ post.id }}</TableCell>
                        <TableCell class="px-6 py-4 text-sm text-gray-800 dark:text-white/90">{{ post.title }}</TableCell>
                        <TableCell class="px-6 py-4 text-sm text-gray-800 dark:text-white/90">
                          <div class="max-w-xs truncate" :title="post.content">
                            {{ stripHtml(post.content) }}
                          </div>
                        </TableCell>
                        <TableCell class="px-6 py-4 text-sm text-gray-800 dark:text-white/90">
                          {{ getCategoryName(post.category_id) }}
                        </TableCell>
                        <TableCell class="px-6 py-4 text-sm font-medium">
                          <div class="flex items-center justify-center space-x-2">
                            <Button as-child size="xs">
                              <router-link :to="'/post-detail/' + post.id">
                                <Pencil class="size-4" />
                                <span>Upraviť</span>
                              </router-link>
                            </Button>
                            <Button variant="destructive" size="xs" @click="confirmDelete(post)">
                              <Trash2 class="size-4" />
                              <span>Zmazať</span>
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                    <TableEmptyState
                      v-if="loaded && !posts.length"
                      title="Žiadne články neboli nájdené"
                      hint="Skúste upraviť hľadanie."
                      colspan="5"
                    />
                  </Table>
                </div>
                <template #footer>
                  <TablePagination
                    v-if="pagination.last_page > 1"
                    :current-page="pagination.current_page"
                    :total-pages="pagination.last_page"
                    @page-change="retrievePosts($event, '')"
                  />
                </template>
              </TableCard>
            </div>

    <!-- Delete Confirmation Modal -->
    <div v-if="showDeleteModal" class="fixed inset-0 bg-gray-600/50 overflow-y-auto h-full w-full z-50 dark:bg-gray-900"
      @click="cancelDelete">
      <div class="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white dark:bg-white/3" @click.stop>
        <div class="mt-3 text-center">
          <div class="mx-auto flex items-center justify-center size-12 rounded-full bg-red-100">
            <svg class="size-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 class="text-lg leading-6 font-medium text-gray-900 mt-4 dark:text-white/90">Potvrdiť vymazanie</h3>
          <div class="mt-2 px-7 py-3">
            <p class="text-sm text-gray-500 dark:text-gray-400">
              Naozaj chcete zmazať článok <strong>"{{ postToDelete?.title }}"</strong>?<br>
              Táto akcia sa nedá vrátiť.
            </p>
          </div>
          <div class="items-center px-4 py-3">
            <div class="flex space-x-3">
              <button @click="cancelDelete"
                class="px-4 py-2 bg-gray-500 text-white text-base font-medium rounded-md w-full shadow-xs hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-300 dark:bg-gray-700 dark:hover:bg-white/5">
                Zrušiť
              </button>
              <button @click="deletePost"
                class="px-4 py-2 bg-red-500 text-white text-base font-medium rounded-md w-full shadow-xs hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-300">
                Zmazať
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </main>
</template>
